import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography, Box, Button, IconButton, Tooltip } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/DeleteSharp";
import pick from "lodash/pick";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { selectUser } from "redux/slices/userSlice";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Alert from "@material-ui/lab/Alert";
import TextField from "@material-ui/core/TextField";
import { useHistory } from "react-router-dom";
import { Input, CustomMultiSelect, Autocomplete as CountryAutocomplete } from "components/Shared/mui-formik-inputs";
import { ZipcodeSchema, ZipcodeCreateSchema } from "constants/validation-schemas";
import { ZipcodeFormAllowedFields } from "constants/forms-submit-allowed-fields";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import ZipcodeCommaSeparatedInput from "components/Zipcodes/ZipcodeCommaSeparatedInput";
import ZipcodeMapPreview, {
  ZIPCODE_MAP_DEFAULT_LATITUDE,
  ZIPCODE_MAP_DEFAULT_LONGITUDE,
} from "components/Zipcodes/ZipcodeMapPreview";
import { parseCommaSeparatedZipcodes } from "util/zipcodeCommaSeparated";
import { resolveZipcodeLngLat } from "util/zipcodeListGroup";
import { getCountryOptions, normalizeCountryCode } from "util/countryDisplay";
import { sortSuppliersByName } from "util/sortSuppliers";
import { previewZipcodeGeocode } from "redux/slices/zipcodeSlice";

function markerFromCoords(zipcode, latitude, longitude, geocodeMatched = true) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!zipcode || !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    return null;
  }
  return {
    zipcode: String(zipcode).trim(),
    latitude: lat,
    longitude: lng,
    geocodeMatched,
    manuallyPlaced: false,
  };
}

function placeholderMarker(zipcode) {
  return {
    zipcode: String(zipcode).trim(),
    latitude: ZIPCODE_MAP_DEFAULT_LATITUDE,
    longitude: ZIPCODE_MAP_DEFAULT_LONGITUDE,
    geocodeMatched: false,
    manuallyPlaced: false,
  };
}

function markerFromInitialValues(initialValues) {
  if (!initialValues) {
    return null;
  }
  const resolved = resolveZipcodeLngLat(initialValues);
  if (!resolved) {
    return null;
  }
  return markerFromCoords(
    initialValues.zipcode,
    resolved.latitude,
    resolved.longitude,
  );
}

function initialMapMarkers(isAdd, initialValues) {
  if (isAdd || !initialValues) {
    return [];
  }
  const marker = markerFromInitialValues(initialValues);
  if (marker) {
    return [marker];
  }
  const zipcode = String(initialValues.zipcode ?? "").trim();
  return zipcode ? [placeholderMarker(zipcode)] : [];
}

const useStyles = makeStyles({
  _heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
  },
  _icons: {
    color: "#ADADAD",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    margin: "0 16px 0 0px",
    fontSize: "35px",
  },
  _save: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#6F9CEB",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _close: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#525252",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _subheading: {
    font: "normal normal 500 22px/32px Roboto",
    color: " #121212",
    marginTop: "44px",
    marginBottom: "40px",
  },
  _dflex: {
    display: "flex",
    alignItems: "center",
  },
});

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function mapMarkersToZipcodeLocations(markers) {
  return (markers || [])
    .filter((marker) => marker.geocodeMatched || marker.manuallyPlaced)
    .map((marker) => ({
      zipcode: marker.zipcode,
      latitude: marker.latitude,
      longitude: marker.longitude,
    }));
}

function parseDebouncedPreviewKey(key) {
  const separator = key.indexOf(":");
  if (separator === -1) {
    return { country: "DE", zipcodes: [] };
  }
  const country = normalizeCountryCode(key.slice(0, separator));
  const zipcodes = key
    .slice(separator + 1)
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return { country, zipcodes };
}

const ZipcodeForm = ({
  initialValues,
  onSubmit,
  action,
  tourList,
  supplierList,
  otherCountryChoices = [],
  onSwitchEditCountry,
}) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const isAdd = action === "ADD";
  const getTourTypeCategory = (tour) => tour?.tour_type_category ?? tour?.tour_category;
  const deliveryTours = (tourList || []).filter((tour) => getTourTypeCategory(tour) !== "pickup");
  const countryOptions = useMemo(() => getCountryOptions(t, i18n), [t, i18n]);
  const sortedSupplierList = useMemo(() => sortSuppliersByName(supplierList), [supplierList]);
  const [mapMarkers, setMapMarkers] = useState(() => initialMapMarkers(isAdd, initialValues));
  const [geocodePreviewResolvedKey, setGeocodePreviewResolvedKey] = useState("");
  const [geocodeFailedZipcodes, setGeocodeFailedZipcodes] = useState([]);
  const previewRequestIdRef = useRef(0);
  const zipcodePreviewEditedRef = useRef(false);

  const user = useSelector(selectUser);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: isAdd ? ZipcodeCreateSchema : ZipcodeSchema,
    initialValues: isAdd
      ? { zipcodes_input: "", supplier_ids: (user?.supplier_id) ? [user.supplier_id] : [], tour_mappings: [{ tour_id: null, departures: [] }], country: "DE" }
      : { zipcode: "", tour_mappings: [{ tour_id: null, departures: [] }], supplier_ids: [], country: "DE", ...initialValues },
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const country = normalizeCountryCode(values.country);
        if (isAdd) {
          await onSubmit({
            zipcodes: parseCommaSeparatedZipcodes(values.zipcodes_input),
            supplier_ids: values.supplier_ids,
            tour_mappings: values.tour_mappings,
            country,
            zipcode_locations: mapMarkersToZipcodeLocations(mapMarkers),
          });
        } else {
          await onSubmit({
            ...pick(values, ZipcodeFormAllowedFields),
            country: normalizeCountryCode(values.country),
            current_country: normalizeCountryCode(initialValues?.current_country),
            current_tour_id: initialValues?.current_tour_id ?? values.tour_mappings?.[0]?.tour_id,
            zipcode_locations: mapMarkersToZipcodeLocations(mapMarkers),
          });
        }
      } catch (err) {
        if (err?.response?.data?.errors) {
          const nextErrors = { ...err.response.data.errors };
          delete nextErrors.zipcode_not_unique_conflicts;
          setErrors(nextErrors);
        }
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    handleChange,
    setFieldValue,
    setErrors,
    validateForm,
    errors,
    handleSubmit,
    submitCount,
    isValid,
    isSubmitting,
  } = formik;
  let { handleBlur } = formik;

  if (!submitCount) {
    handleBlur = null;
  }

  const zipcodesForPreview = useMemo(() => {
    const country = normalizeCountryCode(values.country);
    if (!country) {
      return [];
    }
    if (isAdd) {
      return parseCommaSeparatedZipcodes(values.zipcodes_input);
    }
    const zip = String(values.zipcode ?? "").trim();
    return zip ? [zip] : [];
  }, [isAdd, values.country, values.zipcodes_input, values.zipcode]);

  const currentPreviewKey = `${normalizeCountryCode(values.country)}:${zipcodesForPreview.join(",")}`;
  const debouncedPreviewKey = useDebouncedValue(currentPreviewKey, 1000);

  const isGeocodePreviewPending =
    Boolean(currentPreviewKey && zipcodesForPreview.length > 0) &&
    (currentPreviewKey !== debouncedPreviewKey ||
      geocodePreviewResolvedKey !== debouncedPreviewKey);

  const showGeocodeWarning =
    !isGeocodePreviewPending &&
    geocodeFailedZipcodes.some((zipcode) => {
      const marker = mapMarkers.find((item) => String(item.zipcode) === String(zipcode));
      return !marker?.manuallyPlaced;
    });

  useEffect(() => {
    if (isAdd || !initialValues) {
      return;
    }
    zipcodePreviewEditedRef.current = false;
    const marker = markerFromInitialValues(initialValues);
    const zipcode = String(initialValues?.zipcode ?? "").trim();
    setMapMarkers(marker ? [marker] : zipcode ? [placeholderMarker(zipcode)] : []);
  }, [
    isAdd,
    initialValues?.zipcode,
    initialValues?.country,
    initialValues?.latitude,
    initialValues?.longitude,
  ]);

  useEffect(() => {
    const { country, zipcodes } = parseDebouncedPreviewKey(debouncedPreviewKey);
    if (!country || !zipcodes.length) {
      setMapMarkers([]);
      setGeocodeFailedZipcodes([]);
      setGeocodePreviewResolvedKey(debouncedPreviewKey);
      return undefined;
    }

    if (!isAdd && !zipcodePreviewEditedRef.current) {
      const marker = markerFromInitialValues(initialValues);
      if (marker) {
        setMapMarkers([marker]);
        setGeocodeFailedZipcodes([]);
      } else {
        setMapMarkers(zipcodes.map((zipcode) => placeholderMarker(zipcode)));
        setGeocodeFailedZipcodes(zipcodes);
      }
      setGeocodePreviewResolvedKey(debouncedPreviewKey);
      return undefined;
    }

    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;
    let cancelled = false;

    (async () => {
      const uniqueZipcodes = [...new Set(zipcodes)];
      const markers = [];
      const failed = [];

      await Promise.all(
        uniqueZipcodes.map(async (zipcode) => {
          try {
            const data = await previewZipcodeGeocode(zipcode, country);
            const marker = data?.found === false
              ? null
              : markerFromCoords(zipcode, data?.latitude, data?.longitude);
            if (marker) {
              markers.push(marker);
            } else {
              failed.push(zipcode);
              markers.push(placeholderMarker(zipcode));
            }
          } catch {
            failed.push(zipcode);
          }
        }),
      );

      if (!cancelled && requestId === previewRequestIdRef.current) {
        setMapMarkers(markers);
        setGeocodeFailedZipcodes(failed);
        setGeocodePreviewResolvedKey(debouncedPreviewKey);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedPreviewKey, isAdd, initialValues]);

  const clearServerZipcodeError = () => {
    if (!errors.zipcode) {
      return;
    }
    const next = { ...errors };
    delete next.zipcode;
    setErrors(next);
  };

  const syncAfterServerZipcodeFieldChange = async () => {
    clearServerZipcodeError();
    if (submitCount > 0) {
      await validateForm();
    }
  };

  const handleZipcodesInputChange = (e) => {
    zipcodePreviewEditedRef.current = true;
    handleChange(e);
    clearServerZipcodeError();
  };

  const handleZipcodeChange = (e) => {
    zipcodePreviewEditedRef.current = true;
    handleChange(e);
    clearServerZipcodeError();
  };

  const handleSupplierChange = async (e) => {
    await setFieldValue("supplier_ids", e.target.value, false);
    await syncAfterServerZipcodeFieldChange();
  };

  const handleCountryChange = async (selected) => {
    if (!isAdd) {
      zipcodePreviewEditedRef.current = true;
    }
    await setFieldValue("country", selected?.code ?? "DE", false);
    await syncAfterServerZipcodeFieldChange();
  };

  const handleTourChange = async (opt) => {
    await setFieldValue("tour_id", opt ? opt.value : null, false);
    await syncAfterServerZipcodeFieldChange();
  };

  const handleMarkerMove = useCallback((zipcode, latitude, longitude) => {
    setMapMarkers((prev) =>
      prev.map((marker) =>
        String(marker.zipcode) === String(zipcode)
          ? { ...marker, latitude, longitude, manuallyPlaced: true }
          : marker,
      ),
    );
  }, []);

  const toursOptions = deliveryTours.map((o) => ({
    label: o.name,
    value: o.id,
    disabled: !o.active,
  }));

  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);
  const lastElementRef = useRef(null);

  useEffect(() => {
    const handleTabPress = (e) => {
      if (e.key === "Tab") {
        if (lastElementRef.current && lastElementRef.current.contains(document.activeElement)) {
          e.preventDefault();
          if (cancelButtonRef.current) {
            cancelButtonRef.current.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleTabPress);
    return () => {
      document.removeEventListener("keydown", handleTabPress);
    };
  }, []);

  return (
    <Box height="100%">
      <Box display="flex" mb={4}>
        <Box flex={2}>
          <Typography className={classes._heading} variant="h4">
            {isAdd ? t("New Zipcode") : t("Edit Zipcode")}
          </Typography>
        </Box>
        <Box flex={2} textAlign="right" position="relative">
          <Box position="absolute" right={0}>
            <FormCancelSaveButton
              disabled={!isValid || isSubmitting}
              onCancel={() => history.push(PATHS.zipcodes.root)}
              onSave={handleSubmit}
              ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
            />
          </Box>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          {isAdd ? (
            <ZipcodeCommaSeparatedInput
              label={t("Zipcode")}
              name="zipcodes_input"
              value={values.zipcodes_input}
              onChange={handleZipcodesInputChange}
              errors={errors}
              onBlur={handleBlur}
            />
          ) : (
            <Input
              label={t("Zipcode")}
              name="zipcode"
              onChange={handleZipcodeChange}
              onBlur={handleBlur}
              value={values.zipcode}
              errors={errors}
              inputProps={{
                maxLength: 15,
              }}
              required
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <CountryAutocomplete
            onBlur={handleBlur}
            name="country"
            label={t("Country")}
            errors={errors}
            value={values.country}
            settings={{
              disableClearable: true,
              valueProp: "code",
              labelProp: "name",
            }}
            onChange={handleCountryChange}
            options={countryOptions}
            required
          />
          {/* INFO: Client asked to remove it, we are keeping it here for now for testing purposes */}
          {!isAdd && otherCountryChoices.length > 0 && false && (
            <Box mt={1}>
              {otherCountryChoices.map((choice) => (
                <Typography
                  key={choice.country}
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => onSwitchEditCountry?.(choice.country)}
                  style={{
                    display: "block",
                    background: "none",
                    border: "none",
                    padding: 0,
                    marginBottom: 4,
                    color: "#6F9CEB",
                    cursor: "pointer",
                    textAlign: "left",
                    font: "inherit",
                  }}
                >
                  {t("Edit for country", { countryName: choice.countryName })}
                </Typography>
              ))}
            </Box>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <CustomMultiSelect
            values={sortedSupplierList || []}
            errors={errors}
            onChange={handleSupplierChange}
            setValues={values.supplier_ids}
            label={isAdd ? t("Suppliers") : t("Supplier")}
            labelId="zipcode-suppliers-label"
            backgroundColor="#dfdfdf"
            borderColor="#6F9CEB"
            required
            name="supplier_ids"
          />
        </Grid>
        {Boolean(deliveryTours) && (
          <Grid item xs={12}>
            <Typography variant="h6" className={classes._subheading} style={{ marginTop: "24px", marginBottom: "16px" }}>
              {t("Tour")}
            </Typography>
            {(values.tour_mappings || []).map((mapping, index) => {
              const selectedTour = deliveryTours.find((t) => Number(t.id) === Number(mapping.tour_id));
              const allDepartures = selectedTour
                ? (selectedTour.delivery_timing_configs || [])
                  .map((config) => config.delivery_timing)
                  .filter(Boolean)
                : [];
              const otherSelectedDepartures = (values.tour_mappings || [])
                .filter((_, i) => i !== index)
                .flatMap((m) => m.departures || []);
              const departures = allDepartures.filter((d) => !otherSelectedDepartures.includes(d));

              const filteredTours = deliveryTours.filter((o) => {
                const tourDepartures = (o.delivery_timing_configs || [])
                  .map((config) => config.delivery_timing)
                  .filter(Boolean);
                const hasAvailableDeps = tourDepartures.some((d) => !otherSelectedDepartures.includes(d));
                return hasAvailableDeps || Number(o.id) === Number(mapping.tour_id);
              });

              const rowToursOptions = filteredTours.map((o) => ({
                label: o.name,
                value: o.id,
                disabled: !o.active,
              }));

              return (
                <Grid container spacing={2} key={index} alignItems="center" style={{ marginBottom: "12px" }}>
                  <Grid item xs={12} sm={5} md={4} lg={3}>
                    <Autocomplete
                      id={`tour-autocomplete-${index}`}
                      options={rowToursOptions}
                      getOptionLabel={(option) => option.label}
                      style={{ width: "100%" }}
                      value={rowToursOptions.find((t) => Number(t.value) === Number(mapping.tour_id)) || null}
                      onChange={(evt, opt) => {
                        const newMappings = [...values.tour_mappings];
                        newMappings[index] = {
                          tour_id: opt ? opt.value : null,
                          departures: [],
                        };
                        setFieldValue("tour_mappings", newMappings);
                      }}
                      noOptionsText={t("No options")}
                      disableClearable
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("Tour")}
                          required
                          variant="filled"
                          margin="dense"
                          error={!!(errors.tour_mappings?.[index]?.tour_id)}
                          helperText={
                            errors.tour_mappings?.[index]?.tour_id
                              ? t(errors.tour_mappings[index].tour_id)
                              : undefined
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5} md={4} lg={3}>
                    <CustomMultiSelect
                      values={departures}
                      errors={errors}
                      onChange={(e) => {
                        const newMappings = [...values.tour_mappings];
                        newMappings[index].departures = e.target.value;
                        setFieldValue("tour_mappings", newMappings);
                      }}
                      setValues={mapping.departures || []}
                      label={t("Departures")}
                      labelId={`tour-departures-label-${index}`}
                      backgroundColor="#dfdfdf"
                      borderColor="#6F9CEB"
                      required
                      disabled={!mapping.tour_id}
                      name={`tour_mappings[${index}].departures`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2} md={2}>
                    <IconButton
                      disabled={values.tour_mappings.length <= 1}
                      onClick={() => {
                        const newMappings = values.tour_mappings.filter((_, i) => i !== index);
                        setFieldValue("tour_mappings", newMappings);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
            {(() => {
              const otherSelectedDepartures = (values.tour_mappings || [])
                .flatMap((m) => m.departures || []);
              const availableTours = (deliveryTours || []).filter((o) => {
                const tourDepartures = (o.delivery_timing_configs || [])
                  .map((config) => config.delivery_timing)
                  .filter(Boolean);
                return tourDepartures.some((d) => !otherSelectedDepartures.includes(d));
              });
              const noToursAvailable = availableTours.length === 0;

              return (
                <Tooltip
                  title={noToursAvailable ? t("No tours available to assign") : ""}
                  placement="top"
                >
                  <span>
                    <Button
                      ref={lastElementRef}
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setFieldValue("tour_mappings", [
                          ...values.tour_mappings,
                          { tour_id: null, departures: [] },
                        ]);
                      }}
                      disabled={noToursAvailable}
                      style={{ marginTop: "8px" }}
                    >
                      {t("Add Tour")}
                    </Button>
                  </span>
                </Tooltip>
              );
            })()}
          </Grid>
        )}
      </Grid>
      <Box mt={4}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {showGeocodeWarning && (
              <Box mb={2}>
                <Alert severity="warning">{t("ZIPCODE_GEOCODE_WARNING")}</Alert>
              </Box>
            )}
            <ZipcodeMapPreview
              markers={mapMarkers}
              draggable={mapMarkers.length > 0}
              onMarkerMove={handleMarkerMove}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
export default ZipcodeForm;
